import { inventoryService } from '../../../services/inventoryService';
import { machineService } from '../../../services/machineService';
import { machineTypeService } from '../../../services/machineTypeService';
import { roasterService } from '../../../services/roasterService';
import { recipeService } from '../../../services/recipeService';
import { artisanProfileService, type ArtisanProfileListItemDTO } from '../../../services/artisanProfileService';
import type { InventoryItem } from '../../../types/inventory';
import type { GreenBeanProfile } from '../../../types/greenBean';
import type { Machine } from '../../../types/machine';
import type { Roaster } from '../../../types/roaster';
import type { Recipe } from '../../../types/recipe';

export interface WorkOrderReferenceData {
  recipes: Recipe[];
  greenBeans: InventoryItem[];
  artisanProfiles: ArtisanProfileListItemDTO[];
  machines: Machine[];
  roasters: Roaster[];
}

export interface LotOption {
  id: string;
  name: string;
  remaining: number;
  density?: number | null;
  moisture?: number | null;
  processType?: string | null;
  hasGreenBeanProfile?: boolean;
  greenBeanProfileId?: string;
}

/**
 * Both the single work order form and the bulk cards label lot options the same
 * way; keeping the formatting here stops the two from drifting apart. The grade
 * renders in place on the lot number (IZGB0001-LT1 -> IZGB0001-LT1G1); no grade
 * means no "G".
 */
export const buildLotOptionName = (
  lotNumber: string,
  profile: GreenBeanProfile | undefined,
  remaining: number,
  t: (key: string) => string,
): string => {
  const graded = profile?.grade ? `${lotNumber}G${profile.grade}` : lotNumber;
  const prefix = profile?.process_type ? `${graded} - ${profile.process_type}` : graded;
  return `${prefix} (${t('reports.details.qty')}: ${remaining.toFixed(3)}${t('inventory.history.unitKg')})`;
};

/**
 * The six reference lists every work order form needs. allSettled (not all) so
 * a single 403 — custom roles may hold read on roasters but not artisan
 * profiles — cannot blank the whole form.
 */
export const fetchWorkOrderReferenceData = async (
  includeOnboardingSamples: boolean,
): Promise<WorkOrderReferenceData> => {
  const settled = await Promise.allSettled([
    recipeService.getAll(),
    inventoryService.getAll({ includeOnboardingSamples }),
    artisanProfileService.getAll({ status: 'unsynced' }),
    machineService.getAll(),
    machineTypeService.getActive({ includeOnboardingSamples }),
    roasterService.getAll({ includeOnboardingSamples }),
  ]);

  const valueOr = <T,>(idx: number, fallback: T): T => {
    const result = settled[idx];
    return result.status === 'fulfilled' ? (result.value as T) : fallback;
  };

  const recipes = valueOr<Recipe[]>(0, []);
  const inventory = valueOr<InventoryItem[]>(1, []);
  const artisanProfiles = valueOr<ArtisanProfileListItemDTO[]>(2, []);
  const machineData = valueOr<{ id: string; isSampelRoaster?: boolean }[]>(3, []);
  const typeData = valueOr<
    {
      id: string;
      machineId: string;
      machineName?: string;
      name: string;
      minimumInputKg?: number;
      maximumInputKg?: number;
    }[]
  >(4, []);
  const roasterData = valueOr<Roaster[]>(5, []);

  const machineLookup = new Map(
    (Array.isArray(machineData) ? machineData : []).map((machine) => [machine.id, machine]),
  );

  return {
    recipes: Array.isArray(recipes) ? recipes : [],
    greenBeans: Array.isArray(inventory) ? inventory.filter((i) => i.itemType === 'green_bean') : [],
    artisanProfiles: Array.isArray(artisanProfiles) ? artisanProfiles : [],
    machines: Array.isArray(typeData)
      ? typeData.map((type) => ({
          id: type.id,
          name: type.machineName ? `${type.machineName}${type.name ? ' - ' : ''}${type.name}` : type.name,
          isActive: true,
          isSampelRoaster: machineLookup.get(type.machineId)?.isSampelRoaster ?? false,
          minimumInputKg: type.minimumInputKg,
          maximumInputKg: type.maximumInputKg,
          parentMachineId: type.machineId,
          value: type.id,
        }))
      : [],
    roasters: Array.isArray(roasterData) ? roasterData : [],
  };
};
